import React, {useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useParams, useSearchParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import {FaExternalLinkAlt, FaPrint} from "react-icons/fa";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import RegisteredLocation from "../../../../components/registered-location/registered-location.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import DispatchBoGenerator from "./components/dispatch-bo-generator.jsx";
import DispatchOtherOccurrences from "./components/dispatch-other-occurrences.jsx";
import DispatchDisplacements from "./components/dispatch-displacements.jsx";
import DispatchImageReservation from "./components/dispatch-image-reservation.jsx";
import DispatchClosure from "./components/dispatch-closure.jsx";
import DispatchRefuelling from "./components/dispatch-refuelling.jsx";
import DispatchMaintenance from "./components/dispatch-maintenance.jsx";
import {
    OCCURRENCE_REASON,
    buildAttendanceOption,
    buildDispatchModel,
    buildImageReservation,
    destinyToLocation,
    displacementSummary,
    emptyLocation,
    encounterLinkLabel,
    factToLocation,
    mapGarrisonOptions,
    mapNatureOptions,
    mapRetroactiveGarrisonOptions,
    mapRetroactiveSupportOptions,
    mapStringOptions,
    mapSupportGarrisonOptions,
} from "./utils/dispatch-form-utils.js";
import AlertMessage from "../../../../components/messages/alert-message.js";
import {MaskDefault} from "../../../../utils/mask.js";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import {finishDashboardEdit} from "../../../../utils/dashboard-edit-window.js";
import FormBuilderModal from "../../../web/form-builder/modal/form-builder-modal.jsx";
import {FaFilePen} from "react-icons/fa6";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const API_DESPACHO = import.meta.env.VITE_API_DESPACHO;

const initialState = {
    id: undefined,
    systemUpdate: "",
    start: "",
    end: "",
    finalized: false,
    nature: "",
    numberCheckbook: "",
    supervisionGarrison: "",
    attendanceId: "",
    contactName: "",
    contactPhone: "",
    contactCpf: "",
    transcription: "",
    observation: "",
    initialObservation: "",
    finalObservation: "",
    garrison: "",
    garrisonName: "",
    supportForGarrison: "",
    supportForGarrisonName: "",
    supportForGarrisonDispatch: "",
    activity: false,
    providence: "",
    occurrenceNumber: "",
    occurrenceNumberCode: "",
    otherOccurrenceNumber: [],
    notFound: false,
    publicPatrimony: false,
    policeAuthority: false,
    flagrant: false,
    infracionalAct: false,
    indicted: false,
    notCriminal: false,
    cancelSupport: false,
};

const initialMeta = {
    boName: "BO",
    sizeMaxText: "",
    allowCortexIndividual: false,
    hasSupports: false,
    myGarrison: false,
    allowSupervisionOtherGarrison: false,
    dispatchStatus: true,
    hasExistingSupervision: false,
    attendanceLinked: false,
    encounters: [],
    unuploadedBoMessage: "",
    refuellingLinks: [],
    maintenanceLinks: [],
    entityCity: "",
};

const toSendDate = (value) => {
    if (!value) return "";
    const [datePart, timePart = ""] = value.split(" ");
    const [dd, mm, yyyy] = datePart.split("/");
    if (!dd || !mm || !yyyy) return "";
    return `${yyyy}-${mm}-${dd} ${timePart}`;
};

const DispatchForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const {pathname} = useLocation();
    const [searchParams] = useSearchParams();

    const retroactive = pathname.includes("retroactive");
    const myGarrison = searchParams.get("my-garrison");
    const redir = searchParams.get("redir");
    const isEdit = !!id;

    const [state, setState] = useState(initialState);
    const [meta, setMeta] = useState(initialMeta);
    const [options, setOptions] = useState({
        natures: [],
        providences: [],
        supervisionGarrisons: [],
        garrisonSelf: [],
        supportGarrisons: [],
        reservationCause: [],
        attendances: [],
        reservationAttendances: [],
        cameras: [],
    });

    const [location, setLocation] = useState(emptyLocation());
    const [displacementRows, setDisplacementRows] = useState([]);
    const [imageReservation, setImageReservation] = useState({cause: [], attendanceId: "", dtFact: ""});
    const [cameraRows, setCameraRows] = useState([]);
    const [attaches, setAttaches] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState({});

    const [vehicle, setVehicle] = useState(null);
    const [kmControl, setKmControl] = useState(true);
    const [actualKm, setActualKm] = useState(null);

    const [isSaving, setIsSaving] = useState(false);
    const [isBusy, setIsBusy] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const [formModal, setFormModal] = useState({open: false, object: undefined});

    const tempKey = useRef(0);
    const nextTempId = () => `disp-${tempKey.current++}`;

    const refuellingRef = useRef(null);
    const maintenanceRef = useRef(null);

    const isSupport = !!state.supportForGarrison;
    const showImageReservation = state.nature === OCCURRENCE_REASON.RESERVATION_CAUSE;
    const showSupervision = !retroactive && state.nature === OCCURRENCE_REASON.LEADER_SUPERVISION && (meta.allowSupervisionOtherGarrison || meta.hasExistingSupervision);
    const closeByDash = isEdit && meta.dispatchStatus && (meta.hasSupports || meta.attendanceLinked);
    const showFinalizeRow = !closeByDash;
    const showEnd = (state.finalized || retroactive) && showFinalizeRow;
    const finalizeDisabled = retroactive || (isEdit && !meta.dispatchStatus);
    const showClosure = state.finalized;
    const showAddDisplacement = (!!state.garrison && (!isEdit || meta.dispatchStatus)) || !!myGarrison;
    const attendanceDisabled = isEdit || isSupport;
    const garrisonDisabled = !retroactive;

    const loadGarrisonVehicle = async (garrisonId) => {
        if (!garrisonId) return null;
        try {
            const data = await DispatchService.getGarrisonVehicle(garrisonId);
            setVehicle(data.id);
            setActualKm(data.actualKm);
            setKmControl(data.kmControl);
            return data;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    const initializeDisplacement = (displacementLocation) => {
        setDisplacementRows([{
            tempId: nextTempId(),
            id: "",
            goingDate: "",
            goingMileage: "",
            arrivalDate: "",
            arrivalMileage: "",
            expanded: false,
            location: displacementLocation,
        }]);
    };

    const {isLoading: fetchIsLoading, refetch} = useQuery({
        enable: !!id || retroactive,
        queryKey: ["dispatch-form", id ?? "", retroactive],
        queryFn: async () => {
            let model;

            if (myGarrison) {
                model = await DispatchService.getDispatchMyGarrison(myGarrison);
            } else {
                model = isEdit
                    ? await DispatchService.formData({id, retroactive}).catch(() => null)
                    : await DispatchService.formData({id, retroactive});
            }

            if ((isEdit && !model) || (!isEdit && !retroactive && !myGarrison) || (myGarrison && !model)) {
                navigate("/404");
                return;
            }

            const dispatch = model.dispatch ?? null;
            const dispatchStatus = dispatch ? !!dispatch.status : true;

            setMeta({
                boName: model.boName ?? "BO",
                sizeMaxText: model.sizeMaxText ?? "",
                allowCortexIndividual: !!model.allowCortexIndividual,
                hasSupports: !!model.hasSupports,
                myGarrison: !!model.myGarrison,
                allowSupervisionOtherGarrison: !!model.allowSupervisionOtherGarrison,
                dispatchStatus,
                hasExistingSupervision: !!dispatch?.supervisionGarrison?.id,
                attendanceLinked: !!dispatch?.attendance?.id,
                encounters: model.encounters ?? [],
                unuploadedBoMessage: "",
                refuellingLinks: model.refuellingLinks ?? [],
                maintenanceLinks: model.maintenanceLinks ?? [],
                entityCity: model.entityCity ?? "",
            });

            const garrison = dispatch?.garrison ?? model?.garrison ?? null
            const garrisonSelf = garrison?.id
                ? [{value: garrison.id, label: garrison.name}]
                : [];

            setOptions(prev => ({
                ...prev,
                natures: mapNatureOptions(model.natures),
                providences: mapStringOptions(model.providences),
                reservationCause: mapStringOptions(model.reservationCause),
                supervisionGarrisons: mapGarrisonOptions(model.garrisonBySupervision),
                garrisonSelf,
                supportGarrisons: mapSupportGarrisonOptions(model.garrisonList),
            }));

            try {
                const attendanceData = retroactive
                    ? await DispatchService.allAttendancesClosedByLastHours()
                    : await DispatchService.allAttendances(isEdit ? id : null);
                setOptions(prev => ({...prev, attendances: (attendanceData ?? []).map(buildAttendanceOption)}));
            } catch (error) {
                console.error(error);
            }

            if (isEdit && dispatch) {

                DispatchService.getShouldShowMessageForUnuploadedBo(id)
                    .then(message => setMeta(prev => ({...prev, unuploadedBoMessage: message || ""})))
                    .catch(() => {});

                setState({
                    ...initialState,
                    id,
                    systemUpdate: dispatch.systemUpdate ?? "",
                    start: dispatch.start ?? "",
                    end: dispatch.end ?? "",
                    finalized: !dispatchStatus,
                    nature: dispatch.nature ?? "",
                    numberCheckbook: dispatch.numberCheckbook ?? "",
                    supervisionGarrison: dispatch.supervisionGarrison?.id ?? "",
                    attendanceId: dispatch.attendance?.id ?? "",
                    contactName: dispatch.contactName ?? "",
                    contactPhone: dispatch.contactPhone ?? "",
                    contactCpf: dispatch.contactCpf ?? "",
                    transcription: dispatch.attendance?.transcription ?? "",
                    observation: dispatch.attendance?.observation ?? "",
                    initialObservation: dispatch.initialObservation ?? "",
                    finalObservation: dispatch.finalObservation ?? "",
                    garrison: dispatch.garrison?.id ?? "",
                    garrisonName: dispatch.garrison?.name ?? "",
                    supportForGarrison: dispatch.supportForGarrison?.id ?? "",
                    supportForGarrisonName: dispatch.supportForGarrison?.name ?? "",
                    supportForGarrisonDispatch: dispatch.supportForGarrisonDispatch?.id ?? "",
                    activity: !!dispatch.activity,
                    providence: dispatch.providence ?? "",
                    occurrenceNumber: dispatch.occurrenceNumber ?? "",
                    occurrenceNumberCode: dispatch.occurrenceNumberCode ?? "",
                    otherOccurrenceNumber: dispatch.otherOccurrenceNumber ?? [],
                    notFound: !!dispatch.notFound,
                    publicPatrimony: !!dispatch.publicPatrimony,
                    policeAuthority: !!dispatch.policeAuthority,
                    flagrant: !!dispatch.flagrant,
                    infracionalAct: !!dispatch.infracionalAct,
                    indicted: !!dispatch.indicted,
                    notCriminal: !!dispatch.notCriminal,
                    cancelSupport: !!dispatch.cancelSupport,
                });

                setLocation(factToLocation(dispatch));

                if (Array.isArray(dispatch.attachment)) {
                    const loaded = dispatch.attachment.map(item => ({...item, name: item.name, attachId: item.attachId ?? item.id, new: false}));
                    setAttaches(loaded);
                    setAttachesDescription(loaded.reduce((acc, item) => ({...acc, [item.attachId]: item.description ?? null}), {}));
                }

                await loadGarrisonVehicle(dispatch.garrison?.id);

                setDisplacementRows((dispatch.displacement ?? [])
                    .filter(item => String(item.garrison?.id) === String(dispatch.garrison?.id))
                    .map(item => ({
                        tempId: nextTempId(),
                        id: item.id,
                        goingDate: item.goingDate ?? "",
                        goingMileage: item.goingMileage ?? "",
                        arrivalDate: item.arrivalDate ?? "",
                        arrivalMileage: item.arrivalMileage ?? "",
                        expanded: false,
                        location: destinyToLocation(item),
                    })));

                if (dispatch.nature === OCCURRENCE_REASON.RESERVATION_CAUSE) {
                    if (dispatch.imageReservation) {
                        setImageReservation({
                            cause: dispatch.imageReservation.cause ?? [],
                            attendanceId: dispatch.imageReservation.attendanceId ?? "",
                            dtFact: dispatch.imageReservation.dtFact ?? "",
                        });

                        setCameraRows((dispatch.imageReservation.cameras ?? []).map(camera => ({
                            tempId: nextTempId(),
                            itemId: camera.itemId,
                            cameraId: camera.cameraId ?? "",
                            cameraName: camera.cameraName ?? "",
                            vmsId: camera.vmsId ?? "",
                            dtStart: camera.dtStart ?? "",
                            dtEnd: camera.dtEnd ?? "",
                        })));
                    }
                    await loadImageReservationData(dispatch.start);
                }
            } else if (myGarrison) {
                const garrisonId = model.garrison?.id ?? "";
                const initialLocation = {
                    ...emptyLocation(),
                    city: model?.entityCity ?? ""
                };
                setState({
                    ...initialState,
                    garrison: garrisonId,
                    garrisonName: model.garrison?.name ?? "",
                });

                if (garrisonId) {
                    await loadGarrisonVehicle(garrisonId);
                    initializeDisplacement(initialLocation);
                }

                setLocation(initialLocation);
            } else {
                setState({
                    ...initialState,
                    finalized: true,
                    activity: false,
                });

                setLocation({
                    ...emptyLocation(),
                    city: model?.entityCity ?? ""
                });
                setDisplacementRows([]);
                setImageReservation({cause: [], attendanceId: "", dtFact: ""});
                setCameraRows([]);
                setAttaches([]);
                setAttachesDescription({});
                setVehicle(null);
                setKmControl(true);
                setActualKm(null);
            }

            return model;
        },
    });

    const isSetClose = state.finalized || retroactive;
    const showRefuelling = meta.dispatchStatus && isSetClose && state.nature === OCCURRENCE_REASON.REFUELLING && !!state.garrison;
    const showMaintenance = meta.dispatchStatus && isSetClose && state.nature === OCCURRENCE_REASON.MAINTENANCE && !!state.garrison;
    const initialMileage = displacementRows[0]?.goingMileage ?? "";
    const finalMileage = displacementRows[displacementRows.length - 1]?.arrivalMileage ?? "";
    const suggestedCity = location.city || meta.entityCity;

    // Legacy: the first displacement destination mirrors the fact address (retroactive only;
    // in edit the loaded destinations must not be overwritten).
    useEffect(() => {
        if (isEdit) return;
        setDisplacementRows(prev => prev.length > 0
            ? [{...prev[0], location: {...location}}, ...prev.slice(1)]
            : prev);
    }, [location, isEdit]);

    const loadImageReservationData = async (dispatchStart) => {
        try {
            const [reservationAttendances, cameras] = await Promise.all([
                DispatchService.getAttendancesByImageReservation(projectUtils.formatDate(dispatchStart)).catch(() => []),
                DispatchService.getAllPointsCamera().catch(() => []),
            ]);

            setOptions(prev => ({
                ...prev,
                reservationAttendances: (reservationAttendances ?? []).map(item => ({value: item.id, label: item.name})),
                cameras: (cameras ?? []).map(camera => ({value: camera.id, label: camera.cameraName, vmsId: camera.vmsId})),
            }));
        } catch (error) {
            console.error(error);
        }
    };

    const onChangeGarrison = async (option) => {
        const garrisonId = option?.value ?? "";
        setState(prev => ({...prev, garrison: garrisonId, garrisonName: option?.label ?? ""}));

        if (!garrisonId) {
            setVehicle(null);
            if (retroactive) setDisplacementRows([]);
            return;
        }

        await loadGarrisonVehicle(garrisonId);

        if (retroactive) {
            initializeDisplacement(location);
        }
    };

    const onChangeNature = (option) => {
        const nature = option?.value ?? "";
        setState(prev => ({
            ...prev,
            nature,
            supervisionGarrison: "",
        }));

        if (nature === OCCURRENCE_REASON.RESERVATION_CAUSE && options.cameras.length === 0) {
            loadImageReservationData(state.start);
        }
    };

    const onChangeSupportGarrison = async (option) => {
        const supportId = option?.value ?? "";
        if (!supportId) {
            setState(prev => ({
                ...prev,
                supportForGarrison: "",
                supportForGarrisonDispatch: "",
                nature: prev.nature === OCCURRENCE_REASON.SUPPORT ? "" : prev.nature,
                supervisionGarrison: "",
                cancelSupport: false,
                notFound: false,
                publicPatrimony: false,
                policeAuthority: false,
                flagrant: false,
                infracionalAct: false,
                indicted: false,
                notCriminal: false,
            }));
            return;
        }

        const mainDispatchId = option.supportForGarrison;
        let flags = {};
        if (mainDispatchId) {
            try {
                const mainModel = await DispatchService.formData({id: mainDispatchId});
                const main = mainModel.dispatch ?? {};
                flags = {
                    notFound: !!main.notFound,
                    publicPatrimony: !!main.publicPatrimony,
                    policeAuthority: !!main.policeAuthority,
                    flagrant: !!main.flagrant,
                    infracionalAct: !!main.infracionalAct,
                    indicted: !!main.indicted,
                    notCriminal: !!main.notCriminal,
                };
            } catch (error) {
                console.error(error);
            }
        }

        setState(prev => ({
            ...prev,
            supportForGarrison: supportId,
            supportForGarrisonDispatch: mainDispatchId ?? "",
            nature: OCCURRENCE_REASON.SUPPORT,
            supervisionGarrison: "",
            activity: false,
            attendanceId: "",
            contactName: "",
            contactPhone: "",
            contactCpf: "",
            providence: "",
            otherOccurrenceNumber: [],
            ...flags,
        }));
        setLocation(emptyLocation());
    };

    const onToggleFinalize = async (checked) => {
        if (!checked) {
            setState(prev => ({
                ...prev,
                finalized: false,
                ...(prev.nature === OCCURRENCE_REASON.SUPPORT ? {cancelSupport: false} : {
                    notFound: false, publicPatrimony: false, policeAuthority: false,
                    flagrant: false, infracionalAct: false, indicted: false, notCriminal: false,
                }),
            }));
            return;
        }

        let end = state.end;
        if (!end) {
            end = await DispatchService.getDateFormattedView().catch(() => "");
        }

        setState(prev => ({
            ...prev,
            finalized: true,
            end: end || prev.end,
            ...(prev.nature === OCCURRENCE_REASON.SUPPORT ? {cancelSupport: false} : {
                notFound: false, publicPatrimony: false, policeAuthority: false,
                flagrant: false, infracionalAct: false, indicted: false, notCriminal: false,
            }),
        }));
    };

    const onChangeDateRetroactive = (field, value) => {
        const next = {...state, [field]: value};
        setState(prev => ({...prev, [field]: value}));

        if (!retroactive) return;
        if (!next.start || !next.end) return;
        if (new Date(toSendDate(next.start)).getTime() > new Date(toSendDate(next.end)).getTime()) return;

        DispatchService.getGarrisonListByRetroactive(toSendDate(next.start), toSendDate(next.end))
            .then(data => {
                setOptions(prev => ({
                    ...prev,
                    garrisonSelf: mapRetroactiveGarrisonOptions(data.garrison ?? [], formatViewDate),
                    supportGarrisons: mapRetroactiveSupportOptions(data.garrisonSupport ?? []),
                }));
            })
            .catch(() => projectUtils.swalErrorMessage("Error", "Não foi possível carregar as guarnições e guarnições de apoio"));
    };

    const formatViewDate = (value) => (value ? projectUtils.formatDate(value) : "");

    const cortexSearch = () => {
        if (!meta.allowCortexIndividual) return;
        const cpf = (state.contactCpf ?? "").replace(/[^0-9]+/g, "");
        if (!cpf) {
            projectUtils.swalMessage("warning", "", "Informe o CPF para realizar a pesquisa.", "Ok");
            return;
        }

        setIsBusy(true);
        DispatchService.individualCortexSearch(cpf)
            .then(data => setState(prev => ({...prev, contactName: data.name ?? prev.contactName, contactPhone: data.phone ?? prev.contactPhone})))
            .catch(error => {
                console.error(error);
                const message = error.response?.status !== 500
                    ? (AlertMessage[error.response?.data?.message] ?? "Ocorreu um erro na requisição de dados do indivíduo.")
                    : "Ocorreu um erro na requisição de dados do indivíduo.";
                projectUtils.swalErrorMessage("", message, "Ok");
            })
            .finally(() => setIsBusy(false));
    };

    const setClosureFlag = (partial) => setState(prev => ({...prev, ...partial}));

    const addDisplacement = async () => {
        const last = displacementRows[displacementRows.length - 1];
        const goingDate = retroactive ? (await DispatchService.getDateFormattedView().catch(() => "")) : "";
        setDisplacementRows(prev => [...prev, {
            tempId: nextTempId(),
            id: "",
            goingDate: goingDate ?? "",
            goingMileage: last?.arrivalMileage ?? "",
            arrivalDate: "",
            arrivalMileage: "",
            expanded: false,
            location: {...emptyLocation(), city: suggestedCity},
        }]);
    };

    const addCamera = () => setCameraRows(prev => [...prev, {
        tempId: nextTempId(), itemId: undefined, cameraId: "", cameraName: "", vmsId: "", dtStart: "", dtEnd: "",
    }]);

    const collectMissing = () => {
        const missing = [];
        if (!state.start) missing.push("Data/Hora inicial");
        if ((state.finalized) && !state.end) missing.push("Data/Hora final");
        if (!isSupport && !state.nature) missing.push("Natureza");
        if (!state.garrison) missing.push("Guarnição");
        return [...new Set(missing)];
    };

    const buildFormData = () => {
        const formData = new FormData();

        let refuelling = {list: null, files: []};
        let maintenance = {list: null, files: []};
        if (showRefuelling && refuellingRef.current) refuelling = refuellingRef.current.collect();
        if (showMaintenance && maintenanceRef.current) maintenance = maintenanceRef.current.collect();

        const attachment = attaches
            .filter(file => !file.new)
            .map(file => ({...file, description: attachesDescription[file.attachId] ?? file.description ?? null}));

        const attachmentDescription = attaches
            .filter(file => file.new)
            .map(file => ({name: file.name, description: attachesDescription[file.attachId] ?? null}))
            .filter(item => item.description);

        const reservation = buildImageReservation(imageReservation, cameraRows, state.nature);

        const model = buildDispatchModel({
            state,
            location,
            displacementRows,
            garrisonVehicle: vehicle,
            retroactive,
            actions: {addNew: false, addNewHome: false},
            imageReservation: reservation,
            refuellingList: refuelling.list,
            maintenanceList: maintenance.list,
            attachment,
            attachmentDescription,
        });

        formData.append("myModel", JSON.stringify(model));

        attaches.filter(file => file.new).forEach(file => formData.append("attachFiles", file));
        [...refuelling.files, ...maintenance.files].forEach(item => formData.append(item.field, item.file, item.name));

        return {formData, model};
    };

    const persist = ({addNew = false} = {}) => {
        const missing = collectMissing();
        if (missing.length) {
            projectUtils.swalMessage("warning", "Campos obrigatórios", `Informar: ${missing.join(", ")}`, "Ok");
            return;
        }

        if (showRefuelling && refuellingRef.current && !refuellingRef.current.validate()) return;
        if (showMaintenance && maintenanceRef.current && !maintenanceRef.current.validate()) return;

        const {formData, model} = buildFormData();
        model.addNew = addNew;
        formData.set("myModel", JSON.stringify(model));

        if (retroactive) {
            submit(formData, {addNew});
            return;
        }

        setIsBusy(true);
        DispatchService.verifyMileageBeforeUpsert(model)
            .then(response => {
                setIsBusy(false);
                if (!response.data?.error) {
                    submit(formData, {addNew});
                    return;
                }
            })
            .catch((response) => {

                setIsBusy(false);

                if (response.response?.status !== 406) {
                    projectUtils.swalErrorMessage("Erro", "Erro na verificação do km", "Ok")
                    return;
                }

                const errorMessage = response.response?.data;
                const message = typeof errorMessage === "string" && errorMessage ? errorMessage
                    : "A quilometragem informada é menor que a anterior. Deseja continuar?";

                projectUtils.swalMessage("warning", "", message, "Sim", true, "Não", ({isConfirmed}) => {
                    if (isConfirmed) submit(formData, {addNew});
                }, false, "warning", true);

            });
    };

    const submit = (formData, {addNew}) => {
        if (meta.hasSupports) {
            projectUtils.swalMessage(
                "warning",
                "As seguintes informações serão alteradas: ",
                "- Apoios vinculados: endereço completo do fato",
                "Sim", true, "Não",
                ({isConfirmed}) => { if (isConfirmed) postSubmit(formData, {addNew}); },
            );
        } else {
            postSubmit(formData, {addNew});
        }
    };

    const postSubmit = (formData, {addNew}) => {
        setIsSaving(true);
        DispatchService.upsert(id, formData)
            .then(() => {

                if (addNew) {
                    projectUtils.success();
                    document.getElementById("page-wrapper")?.scrollTo({top: 0, behavior: "smooth"});
                    setIsBusy(true);
                    refetch().finally(() => setIsBusy(false));
                    return;
                }

                projectUtils.success(true);

                if (meta.myGarrison) {
                    navigate(`${API_DESPACHO_WEB}/garrison/form/${state.garrison}`, false);
                    return;
                }

                if (redir) {
                    if (isEdit && finishDashboardEdit(redir, navigate, "Despacho salvo com sucesso.")) return;
                    navigate(`/${redir}`, false);
                    return;
                }

                navigate(API_DESPACHO_WEB + "/dispatch", false);
            })
            .catch(error => {
                console.error(error);
                let message = "Não foi possível editar.";
                if (error.response?.status !== 500) {
                    message = error.response?.data?.errorMessages?.[0] ?? message;
                }
                const useHtml = /<[a-z][\s\S]*>/i.test(message);
                projectUtils.swalErrorMessage("", message, "Ok", false, "Não", undefined, useHtml);
            })
            .finally(() => setIsSaving(false));
    };

    const printDispatch = () => navigate(`/reports/dispatch_info?id=${id}`, true);
    const openGarrison = (id) => navigate(`${API_DESPACHO_WEB}/garrison/form/${id}`, true);
    const openDispatch = (dispatchId) => navigate(`${API_DESPACHO_WEB}/dispatch/form/${dispatchId}`, true);
    const openAttendance = (id) => navigate(`${API_DESPACHO_WEB}/attendance/form/${id}`, true);
    const openEncounter = (encounterId) => navigate(`/encounter/form/${encounterId}`, true);

    const natureValue = useMemo(
        () => options.natures.find(option => option.value === state.nature)
            ?? (state.nature ? {value: state.nature, label: state.nature} : null),
        [options.natures, state.nature],
    );
    const supervisionValue = options.supervisionGarrisons.find(option => String(option.value) === String(state.supervisionGarrison)) ?? null;
    const garrisonValue = options.garrisonSelf.find(option => String(option.value) === String(state.garrison)) ?? null;
    const supportValue = options.supportGarrisons.find(option => String(option.value) === String(state.supportForGarrison)) ?? null;
    const attendanceValue = options.attendances.find(option => String(option.value) === String(state.attendanceId)) ?? null;

    const onChangeAttendance = (option) => {
        const attendanceId = option?.value ?? "";
        setState(prev => ({...prev, attendanceId}));

        if (!attendanceId) {
            setState(prev => ({...prev, transcription: "", observation: ""}));
            setMeta(prev => ({...prev, attendanceLinked: false}));
            return;
        }

        setIsBusy(true);
        DispatchService.attendanceById(attendanceId)
            .then(data => {
                setState(prev => ({
                    ...prev,
                    contactName: data.contactName ?? prev.contactName,
                    contactPhone: data.contactPhone ?? prev.contactPhone,
                    contactCpf: data.contactCpf ?? prev.contactCpf,
                    transcription: data.transcription ?? "",
                    observation: data.observation ?? "",
                }));
                setLocation(prev => ({
                    ...factToLocation(data),
                    sectors: data.sectors ?? prev.sectors,
                }));
                setMeta(prev => ({...prev, attendanceLinked: true}));
            })
            .catch(error => console.error(error))
            .finally(() => setIsBusy(false));
    };

    const openFormModal = () => {
        setFormModal({open: true, object: state});
    }

    return (
        <Template isLoading={fetchIsLoading || isLoading || isSaving || isBusy}>
            <Template.Header>
                <Template.Title browserTitle={'Despacho'}>Despacho {isEdit ? <span className="text-[12px]">(ID: {id})</span> : ""}</Template.Title>
                {isEdit &&
                    <div className={"flex gap-2"}>
                        <Button title={"Formulários"} onClick={openFormModal}><FaFilePen /></Button>
                        <Button title="Imprimir" target="primary" onClick={printDispatch}><FaPrint/></Button>
                    </div>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                {retroactive &&
                    <div className="rounded-md bg-red-100 text-red-700 px-4 py-2 text-sm">
                        O cadastro retroativo deve ser utilizado apenas para cadastrar despachos já encerrados!
                    </div>
                }

                <Card>
                    <Card.Body className="flex flex-col gap-4">
                        <SentryRow className="items-center">
                            <InputDatetime
                                classNameWrapper={"col-span-3 " + REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="start"
                                label="Data/Hora inicial *"
                                dateFormat="LL"
                                returnDateObj={false}
                                selected={state.start}
                                onChange={(value) => onChangeDateRetroactive("start", value ?? "")}
                            />
                            {showFinalizeRow ? (
                                <>
                                    {showEnd &&
                                        <InputDatetime
                                            classNameWrapper={"col-span-3 " + REQUIRED_BG}
                                            labelClass={REQUIRED_LABEl_BG}
                                            id="end"
                                            label="Data/Hora final *"
                                            dateFormat="LL"
                                            returnDateObj={false}
                                            selected={state.end}
                                            onChange={(value) => onChangeDateRetroactive("end", value ?? "")}
                                        />
                                    }
                                    <div className="col-span-3 col-start-10 flex justify-end">
                                        <InputCheckbox
                                            id="finalized"
                                            label="Encerrar despacho"
                                            classNameLabel="font-bold text-sm"
                                            checked={state.finalized}
                                            disabled={finalizeDisabled}
                                            onChange={({target}) => onToggleFinalize(target.checked)}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="col-span-9 flex items-center">
                                    <div className="w-full rounded-md bg-yellow-100 text-yellow-800 px-4 py-2 text-sm">
                                        O despacho deve ser encerrado no dashboard
                                        {meta.hasSupports && meta.attendanceLinked ? " por conter um apoio ativo e estar vinculado a um atendimento"
                                            : meta.hasSupports ? " por conter um apoio ativo"
                                            : meta.attendanceLinked ? " por estar vinculado a um atendimento" : ""}
                                    </div>
                                </div>
                            )}
                        </SentryRow>

                        <SentryRow className="items-end">
                            <SelectComponent
                                classNameWrapper={"col-span-4 " + REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                label="Natureza *"
                                options={options.natures}
                                value={natureValue}
                                isDisabled={isSupport}
                                onChange={onChangeNature}
                            />
                            <InputForm
                                classNameWrapper="col-span-2"
                                id="numberCheckbook"
                                label="N° talão"
                                value={state.numberCheckbook}
                                disabled
                            />
                            {showSupervision &&
                                <div className={"col-span-4 flex flex-between gap-2"}>
                                    <SelectComponent
                                        classNameWrapper="w-full"
                                        label="Fiscalizando a guarnição"
                                        options={options.supervisionGarrisons}
                                        value={supervisionValue}
                                        onChange={(option) => setState(prev => ({...prev, supervisionGarrison: option?.value ?? ""}))}
                                    />

                                    {
                                        isEdit && !!supervisionValue &&
                                        <Button type="button" target="primary-outline" title="Abrir guarnição" onClick={() => openGarrison(supervisionValue?.value)}>
                                            <FaExternalLinkAlt/>
                                        </Button>
                                    }
                                </div>
                            }
                            {isEdit &&
                                <DispatchBoGenerator
                                    dispatchId={id}
                                    boName={meta.boName}
                                    occurrenceNumber={state.occurrenceNumber}
                                    occurrenceNumberCode={state.occurrenceNumberCode}
                                    activity={state.activity}
                                    natureLabel={natureValue?.label ?? ""}
                                    garrisonLabel={state.garrisonName}
                                    addressLabel={displacementSummary(displacementRows[displacementRows.length - 1]?.location)}
                                    onGenerated={(number, code) => setState(prev => ({...prev, occurrenceNumber: number, occurrenceNumberCode: code}))}
                                />
                            }
                        </SentryRow>

                        {showFinalizeRow && state.finalized && meta.unuploadedBoMessage &&
                            <div className="rounded-md bg-yellow-100 text-yellow-800 px-4 py-2 text-sm">
                                {meta.unuploadedBoMessage}
                            </div>
                        }

                        {(meta.refuellingLinks.length > 0 || meta.maintenanceLinks.length > 0 || meta.encounters.length > 0) &&
                            <div className="flex flex-col gap-1 text-sm">
                                {meta.refuellingLinks.length > 0 &&
                                    <span>Ver Abastecimentos: {meta.refuellingLinks.map((link, index) => (
                                        <React.Fragment key={link.id}>
                                            {index > 0 ? " | " : " "}
                                            <button type="button" className="text-blue-600 inline-flex items-center gap-1 cursor-pointer"
                                                    onClick={() => navigate(`/supplies/form/${link.id}`, true)}>
                                                {link.name} <FaExternalLinkAlt size={10}/>
                                            </button>
                                        </React.Fragment>
                                    ))}</span>
                                }
                                {meta.maintenanceLinks.length > 0 &&
                                    <span>Ver manutenções: {meta.maintenanceLinks.map((link, index) => (
                                        <React.Fragment key={link.id}>
                                            {index > 0 ? " | " : " "}
                                            <button type="button" className="text-blue-600 inline-flex items-center gap-1 cursor-pointer"
                                                    onClick={() => navigate(`/maintenances/form/${link.id}`, true)}>
                                                {link.name} <FaExternalLinkAlt size={10}/>
                                            </button>
                                        </React.Fragment>
                                    ))}</span>
                                }
                                {meta.encounters.length > 0 &&
                                    <span>Ver abordagens: {meta.encounters.map((encounter, index) => (
                                        <React.Fragment key={encounter.encounterId}>
                                            {index > 0 ? " | " : " "}
                                            <button type="button" className="text-blue-600 inline-flex items-center gap-1 cursor-pointer"
                                                    onClick={() => openEncounter(encounter.encounterId)}>
                                                {encounterLinkLabel(encounter)} <FaExternalLinkAlt size={10}/>
                                            </button>
                                        </React.Fragment>
                                    ))}</span>
                                }
                            </div>
                        }

                        <SentryRow className="items-end">
                                <div className={"col-span-6 flex justify-between gap-2"}>
                                    <SelectComponent
                                        classNameWrapper="w-full"
                                        label="Atendimento"
                                        options={options.attendances}
                                        value={attendanceValue}
                                        wrapValues
                                        isDisabled={attendanceDisabled}
                                        onChange={onChangeAttendance}
                                    />

                                    {
                                        isEdit && !!attendanceValue &&
                                        <Button type="button" target="primary-outline" title="Abrir atendimento" onClick={() => openAttendance(attendanceValue?.value)}>
                                            <FaExternalLinkAlt/>
                                        </Button>
                                    }
                                </div>
                                <InputForm
                                    classNameWrapper="col-span-2"
                                    id="contactName"
                                    label="Solicitante"
                                    value={state.contactName}
                                    disabled={meta.attendanceLinked || isSupport}
                                    onChange={({target}) => setState(prev => ({...prev, contactName: target.value}))}
                                />
                                <InputForm
                                    classNameWrapper="col-span-2"
                                    id="contactPhone"
                                    label="Telefone"
                                    mask={MaskDefault.phone}
                                    value={state.contactPhone}
                                    disabled={meta.attendanceLinked || isSupport}
                                    onChange={({target}) => setState(prev => ({...prev, contactPhone: target.value}))}
                                />
                                <div className="col-span-2 flex gap-2 items-end">
                                    <InputForm
                                        classNameWrapper="flex-1"
                                        id="contactCpf"
                                        label="CPF"
                                        mask={MaskDefault.cpf}
                                        value={state.contactCpf}
                                        disabled={meta.attendanceLinked || isSupport}
                                        onChange={({target}) => setState(prev => ({...prev, contactCpf: target.value}))}
                                    />
                                    <Button type="button" className={"w-9 p-1!"} target="primary-outline" title="Pesquisar no Córtex" disabled={!meta.allowCortexIndividual || isSupport} onClick={cortexSearch}>
                                        {meta.allowCortexIndividual && <img className={"w-full"} src={getAssetPath("/img/cortex-enabled.svg")} alt=""/>}
                                        {!meta.allowCortexIndividual && <img className={"w-full"} src={getAssetPath("/img/cortex-disabled.svg")} alt=""/>}
                                    </Button>
                                </div>
                        </SentryRow>

                        {meta.attendanceLinked && (state.transcription || state.observation) &&
                            <SentryRow>
                                <InputTextarea
                                    className="col-span-6"
                                    id="transcription"
                                    label="Transcrição do atendimento"
                                    textAreaClass="h-25!"
                                    value={state.transcription}
                                    disabled
                                />
                                <InputTextarea
                                    className="col-span-6"
                                    id="observation"
                                    label="Observação do atendente"
                                    textAreaClass="h-25!"
                                    value={state.observation}
                                    disabled
                                />
                            </SentryRow>
                        }

                        <InputTextarea
                            id="initialObservation"
                            label="Observação inicial"
                            textAreaClass="h-25!"
                            value={state.initialObservation}
                            onChange={({target: {value}}) => setState(prev => ({...prev, initialObservation: value}))}
                        />

                        <SentryRow>
                            <div className={"col-span-6 flex flex-between gap-2"}>
                                <SelectComponent
                                    classNameWrapper={"w-full " + REQUIRED_BG}
                                    labelClass={REQUIRED_LABEl_BG}
                                    label={<span>Guarnição * {retroactive && (!state.start || !state.end) && <small className={"text-red-500"}>(Selecione o horário para carregar as guarnições)</small>}</span>}
                                    options={options.garrisonSelf}
                                    value={garrisonValue}
                                    isDisabled={garrisonDisabled}
                                    onChange={onChangeGarrison}
                                />

                                {
                                    isEdit && !!garrisonValue &&
                                    <Button type="button" target="primary-outline" title="Abrir guarnição" onClick={() => openGarrison(garrisonValue?.value)}>
                                        <FaExternalLinkAlt/>
                                    </Button>
                                }
                            </div>
                            {isEdit ? (
                                <div className={"col-span-6 flex flex-between gap-2"}>
                                    <InputForm
                                        classNameWrapper="w-full"
                                        id="supportForGarrisonName"
                                        label="Apoiando a guarnição"
                                        value={state.supportForGarrisonName}
                                        disabled
                                    />

                                    {
                                        !!state.supportForGarrisonDispatch &&
                                        <Button type="button" target="primary-outline" title="Abrir despacho apoiado" onClick={() => openDispatch(state.supportForGarrisonDispatch)}>
                                            <FaExternalLinkAlt/>
                                        </Button>
                                    }
                                </div>
                            ) : (
                                <div className={"col-span-6 flex flex-between gap-2"}>
                                    <SelectComponent
                                        classNameWrapper="w-full"
                                        label={<span>Apoiando a guarnição {retroactive && (!state.start || !state.end) && <small className={"text-red-500"}>(Selecione o horário para carregar as guarnições)</small>}</span>}
                                        options={options.supportGarrisons}
                                        value={supportValue}
                                        isDisabled={!!state.attendanceId}
                                        onChange={onChangeSupportGarrison}
                                    />
                                </div>
                            )}
                        </SentryRow>
                    </Card.Body>
                </Card>

                {!isSupport &&
                    <RegisteredLocation
                        title="Endereço do fato"
                        data={location}
                        setData={setLocation}
                        showSectors
                        storePlaceName
                        coordinatesRequired={false}
                        disabled={meta.attendanceLinked}
                    />
                }

                <DispatchDisplacements
                    rows={displacementRows}
                    setRows={setDisplacementRows}
                    kmControl={kmControl}
                    showAdd={showAddDisplacement}
                    onAdd={addDisplacement}
                    suggestedCity={suggestedCity}
                />

                {showImageReservation &&
                    <DispatchImageReservation
                        value={imageReservation}
                        onChange={(partial) => setImageReservation(prev => ({...prev, ...partial}))}
                        cameraRows={cameraRows}
                        setCameraRows={setCameraRows}
                        causeOptions={options.reservationCause}
                        attendanceOptions={options.reservationAttendances}
                        cameraOptions={options.cameras}
                        onAddCamera={addCamera}
                    />
                }

                {!isSupport &&
                    <DispatchOtherOccurrences
                        values={state.otherOccurrenceNumber}
                        onChange={(values) => setState(prev => ({...prev, otherOccurrenceNumber: values}))}
                    />
                }

                <Dropzone
                    title="Anexos"
                    attaches={attaches}
                    setAttaches={setAttaches}
                    attachesDescription={attachesDescription}
                    setAttachesDescription={setAttachesDescription}
                    attachUrl={API_DESPACHO + "/attachment/get/"}
                    pdfUrl={API_DESPACHO + "/attachment/print/"}
                />

                {showClosure &&
                    <DispatchClosure
                        flags={state}
                        setFlag={setClosureFlag}
                        providence={state.providence}
                        onProvidenceChange={(value) => setState(prev => ({...prev, providence: value}))}
                        providenceOptions={options.providences}
                        finalObservation={state.finalObservation}
                        onFinalObservationChange={(value) => setState(prev => ({...prev, finalObservation: value}))}
                        isSupport={isSupport}
                        showCancelSupport={state.nature === OCCURRENCE_REASON.SUPPORT}
                    />
                }

                {
                    showRefuelling &&
                    <Card>
                        <Card.Header>
                            <Card.Title>Abastecimento</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <DispatchRefuelling ref={refuellingRef} finalMileage={finalMileage} dispatchId={isEdit ? id : null} garrisonId={state.garrison}/>
                        </Card.Body>
                    </Card>
                }

                {showMaintenance &&
                    <DispatchMaintenance ref={maintenanceRef} initialMileage={initialMileage} finalMileage={finalMileage} dispatchId={isEdit ? id : null} garrisonId={state.garrison}/>
                }

                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {retroactive &&
                            <Button target="primary-outline" onClick={() => persist({addNew: true})} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        }
                        <Button onClick={() => persist()} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>

                <FormBuilderModal
                    module={"DISPATCH"}
                    object={formModal.object}
                    open={formModal.open}
                    externalId={id}
                    setOpen={(open) => setFormModal(prev => ({...prev, open}))}
                    setIsLoading={setIsLoading}
                />
            </Template.Body>
        </Template>
    );
};

export default DispatchForm;